import React, { useState, useEffect } from 'react';
import { Edit2 } from 'lucide-react';
import IconButton from './IconButton';
import Button from './Button';
import Input from './Input';

const InlineNameEditor = ({
  value,
  onSave,
  onCancel,
  label,
  placeholder = 'Enter name',
  disabled = false,
  className = ''
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Update editedValue when value prop changes (but not when editing)
  useEffect(() => {
    if (!isEditing) {
      setEditedValue(value || '');
    }
  }, [value, isEditing]);

  const handleEdit = () => {
    setEditedValue(value || '');
    setError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedValue(value || '');
    setError('');
    setIsEditing(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleSave = async () => {
    const trimmedValue = editedValue.trim();
    
    if (!trimmedValue) {
      setError('Name is required');
      return;
    }

    setIsSaving(true);
    setError('');
    
    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to save name');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className={`flex gap-2 items-center ${className}`}>
        <div className="flex-1">
          {label && (
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {label}
            </label>
          )}
          <Input
            value={editedValue}
            onChange={(e) => setEditedValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSaving}
            error={!!error}
            autoFocus
          />
          {error && (
            <p className="mt-1 text-sm text-red-600">{error}</p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant="primary"
            size="md"
            onClick={handleSave}
            disabled={disabled || isSaving}
            loading={isSaving}
          >
            Save
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={handleCancel}
            disabled={disabled || isSaving}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className={`text-gray-900 ${className}`}>{value || 'No name'}</span>
      <IconButton
        icon={Edit2}
        variant="ghost"
        size="sm"
        onClick={handleEdit}
        disabled={disabled}
        title="Edit name"
      />
    </div>
  );
};

export default InlineNameEditor;

